import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminMgmtApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const ROLE_META = {
  SUPER_ADMIN: { color: '#ef4444', label: 'Super Admin', icon: '👑' },
  MANAGER:     { color: '#6366f1', label: 'Manager',     icon: '🧑‍💼' },
  MARKETING:   { color: '#f59e0b', label: 'Marketing',   icon: '📢' },
  SUPPORT:     { color: '#22c55e', label: 'Support',     icon: '🎧' },
};

// ─── Add Admin Modal ───────────────────────────────────────────────────────────
const AdminModal = ({ onClose, onSaved }) => {
  const [email,     setEmail]     = useState('');
  const [adminRole, setAdminRole] = useState('SUPPORT');
  const [loading,   setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Email is required'); return; }
    setLoading(true);
    try {
      await adminMgmtApi.create({ email: email.trim(), adminRole });
      toast.success('Admin access granted!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add admin');
    } finally { setLoading(false); }
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '440px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>➕ Grant Admin Access</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
            The user must already be registered in the WalkBattle app. Enter their email and select a role.
          </p>
          <div style={fgS}>
            <label style={labelS}>User Email *</label>
            <input style={inputS} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" required />
          </div>
          <div style={fgS}>
            <label style={labelS}>Admin Role *</label>
            <select style={inputS} value={adminRole} onChange={e => setAdminRole(e.target.value)}>
              {Object.entries(ROLE_META).filter(([k]) => k !== 'SUPER_ADMIN').map(([key, m]) => (
                <option key={key} value={key}>{m.icon} {m.label}</option>
              ))}
              <option value="SUPER_ADMIN">👑 Super Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading} style={primaryBtnS}>
              {loading ? 'Granting...' : 'Grant Access'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Role Modal ───────────────────────────────────────────────────────────
const EditRoleModal = ({ admin, onClose, onSaved }) => {
  const [role, setRole] = useState(admin.adminRole || 'SUPPORT');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminMgmtApi.updateRole(admin.id, role);
      toast.success('Role updated!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally { setLoading(false); }
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '360px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>✏️ Change Role</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>
          <p style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: '14px' }}>
            Changing role for <strong>{admin.name}</strong>
          </p>
          <select style={{ ...inputS, marginBottom: '20px' }} value={role} onChange={e => setRole(e.target.value)}>
            {Object.entries(ROLE_META).map(([key, m]) => (
              <option key={key} value={key}>{m.icon} {m.label}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button disabled={loading} style={primaryBtnS} onClick={handleSave}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────────
const AdminManagement = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const { addLog } = useActivity();

  const [admins,    setAdmins]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [showAdd,   setShowAdd]   = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  const fetchAdmins = useCallback(() => {
    setLoading(true);
    adminMgmtApi.getAll()
      .then(res => setAdmins(res.data?.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load admins'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleRevoke = async (admin) => {
    if (admin.id === currentUser.userId) { toast.error("You cannot revoke your own access!"); return; }
    if (!window.confirm(`Revoke admin access for "${admin.name}"?`)) return;
    try {
      await adminMgmtApi.revoke(admin.id);
      toast.success('Admin access revoked');
      addLog('Admin Revoked', admin.email, 'delete');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke');
    }
  };

  const filtered = admins.filter(a =>
    !search ||
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.email?.toLowerCase().includes(search.toLowerCase()) ||
    a.adminRole?.toLowerCase().includes(search.toLowerCase())
  );

  const roleCounts = Object.keys(ROLE_META).reduce((acc, r) => ({
    ...acc, [r]: admins.filter(a => a.adminRole === r).length,
  }), {});

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Management</span> / <span className="current">Admins</span></div>
          <h1>Admin Management</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Manage admin accounts and their role assignments</p>
        </div>
        {isSuperAdmin && (
          <button style={primaryBtnS} onClick={() => setShowAdd(true)}>➕ Add Admin</button>
        )}
      </div>

      {/* Role summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        {Object.entries(ROLE_META).map(([key, m]) => (
          <div key={key} style={{ background: 'var(--bg-card)', border: `1px solid ${m.color}30`, borderTop: `3px solid ${m.color}`, borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>{m.icon}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: m.color }}>{roleCounts[key] || 0}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.label}</div>
          </div>
        ))}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderTop: '3px solid var(--accent-primary)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🏁</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--accent-primary)' }}>{admins.length}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total Admins</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <input style={{ ...inputS, maxWidth: '360px' }}
          placeholder="🔍 Search by name, email or role..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          <div style={{ ...spinS, margin: '0 auto 12px' }} /> Loading admins...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
          {search ? 'No admins match your search.' : 'No admins found.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {filtered.map(admin => {
            const m       = ROLE_META[admin.adminRole] || ROLE_META.SUPPORT;
            const initials = admin.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
            const isMe    = admin.id === currentUser.userId;

            return (
              <div key={admin.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderLeft: `4px solid ${m.color}`, borderRadius: '12px', padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: `linear-gradient(135deg, ${m.color}, ${m.color}99)`, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>
                    {admin.profilePic
                      ? <img src={admin.profilePic} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700 }}>{admin.name}</span>
                      {isMe && <span style={{ fontSize: '0.7rem', background: 'rgba(99,102,241,0.2)', color: 'var(--accent-primary)', padding: '2px 8px', borderRadius: '20px', fontWeight: 600 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin.email}</div>
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', background: m.color + '20', color: m.color, padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                        {m.icon} {m.label}
                      </span>
                      {admin.city && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '3px 8px', borderRadius: '20px' }}>📍 {admin.city}</span>}
                      {admin.status && <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '20px', background: admin.status === 'ACTIVE' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: admin.status === 'ACTIVE' ? '#22c55e' : '#f59e0b' }}>{admin.status}</span>}
                    </div>
                  </div>
                  {isSuperAdmin && !isMe && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                      <button style={{ ...actionBtnS, color: 'var(--accent-primary)' }} onClick={() => setEditAdmin(admin)}>✏️ Role</button>
                      <button style={{ ...actionBtnS, color: 'var(--accent-danger)' }} onClick={() => handleRevoke(admin)}>🚫 Revoke</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isSuperAdmin && (
        <div style={{ marginTop: '24px', padding: '14px 18px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', fontSize: '0.84rem', color: '#f59e0b' }}>
          ⚠️ Only Super Admins can add, change roles, or revoke admin access.
        </div>
      )}

      {showAdd  && <AdminModal    onClose={() => setShowAdd(false)}    onSaved={() => { fetchAdmins(); addLog('Admin Added', 'New admin granted', 'create'); }} />}
      {editAdmin && <EditRoleModal admin={editAdmin} onClose={() => setEditAdmin(null)} onSaved={() => { fetchAdmins(); addLog('Admin Role Changed', editAdmin.email, 'update'); }} />}
    </div>
  );
};

const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS      = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS   = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%' };
const fgS         = { marginBottom: '16px' };
const labelS      = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS  = { padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: 'var(--bg-tertiary)', whiteSpace: 'nowrap' };
const spinS       = { width: '24px', height: '24px', border: '3px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' };

export default AdminManagement;
